import React from 'react'
import classnames from 'classnames'

import {
  ISetActivePagePayload,
} from '@SiteModules/Termbase/types'

const MAX_TOTAL_NUMBER_OF_PAGINATION_ITEMS = 7 // first, last, two siblings around current, and possible two dots
const DOT_LEFT = 'LEFT_ELLIPSIS'
const DOT_RIGHT = 'RIGHT_ELLIPSIS'
const ITEMS_PER_PAGE = 6 // As set in paginates_per in app/models/termbase_entry.tsx

interface TermbasePaginationProps {
  readonly isBusy: boolean
  readonly activePage: number
  readonly totalPages: number
  readonly totalTermbaseEntryCount: number
  setActivePage(payload: ISetActivePagePayload): void
}

const TermbasePagination: React.FC<TermbasePaginationProps> = props => {
  const {
    isBusy,
    activePage,
    totalPages,
    totalTermbaseEntryCount
  } = props

  const isEmpty = totalTermbaseEntryCount === 0
  const min = (activePage - 1) * ITEMS_PER_PAGE + 1
  const max = Math.min(activePage * ITEMS_PER_PAGE, totalTermbaseEntryCount)
  const showingText = `${min}-${max}`

  const getPaginationRange = (active: number, total: number) => {
    // Returns an array of page numbers and string markers 'LEFT_ELLIPSIS' / 'RIGHT_ELLIPSIS'
    // Keeps the list compact for large total pages.
    if (total <= MAX_TOTAL_NUMBER_OF_PAGINATION_ITEMS) {
      return Array.from({ length: total }, (_, i) => i + 1)
    }

    const leftSibling = Math.max(active - 1, 2)
    const rightSibling = Math.min(active + 1, total - 1)

    const showLeftEllipsis = leftSibling > 2
    const showRightEllipsis = rightSibling < total - 1

    const pages: (number | string)[] = []
    pages.push(1)

    if (showLeftEllipsis) {
      pages.push(DOT_LEFT)
    } else {
      for (let i = 2; i < leftSibling; i++) {
        pages.push(i)
      }
    }

    for (let i = leftSibling; i <= rightSibling; i++) {
      pages.push(i)
    }

    if (showRightEllipsis) {
      pages.push(DOT_RIGHT)
    } else {
      for (let i = rightSibling + 1; i <= total - 1; i++) {
        pages.push(i)
      }
    }

    pages.push(total)
    return pages
  }

  const pagesToShow = getPaginationRange(activePage, totalPages)

  const handleSetPage = (page: number) => {
    if (isBusy || page === activePage) { return }
    props.setActivePage({ activePage: page })
  }

  const handlePrev = () => {
    if (isBusy || activePage <= 1) { return }
    props.setActivePage({ activePage: activePage - 1 })
  }

  const handleNext = () => {
    if (isBusy || activePage >= totalPages) { return }
    props.setActivePage({ activePage: activePage + 1 })
  }

  const renderEmpty = () => {
    return (
      <div className="TermbasesFormBody_pagination-wrapper">
        <div className="TermbasesFormBody_pagination-left">Showing 0 of 0 items</div>
      </div>
    )
  }

  const renderPagination = () => {
    return (
      <div className="TermbasesFormBody_pagination-wrapper" role="navigation" aria-label="Termbase pagination">
        <div className="TermbasesFormBody_pagination-left">
          {`Showing ${showingText} of ${totalTermbaseEntryCount} items`}
        </div>

        <div className="TermbasesFormBody_pagination-right">
          <button
            className="TermbasesFormBody_pagination-nav"
            onClick={handlePrev}
            aria-label="Previous page"
            disabled={activePage <= 1 || isBusy}
            type="button"
          >
            ‹
          </button>

          <ul className="TermbasesFormBody_pagination-pages" aria-hidden={false}>
            {pagesToShow.map((p, idx) => {
              if (typeof p === 'string') {
                return (
                  <li
                    key={`dot-${idx}`}
                    className="TermbasesFormBody_pagination-ellipsis"
                    aria-hidden="true"
                  >
                    …
                  </li>
                )
              }

              const isActive = p === activePage
              const setPage = () => handleSetPage(p)

              return (
                <li
                  key={p}
                  className={classnames(
                    'TermbasesFormBody_pagination-page',
                    { 'TermbasesFormBody_pagination-page-active': isActive }
                  )}
                  onClick={setPage}
                  role="button"
                  aria-current={isActive ? 'page' : undefined}
                  aria-label={isActive ? `Page ${p}, current` : `Go to page ${p}`}
                >
                  <span>{p}</span>
                </li>
              )
            })}
          </ul>

          <button
            className="TermbasesFormBody_pagination-nav"
            onClick={handleNext}
            aria-label="Next page"
            disabled={activePage >= totalPages || isBusy}
            type="button"
          >
            ›
          </button>
        </div>
      </div>
    )
  }

  return (
    <React.Fragment>
      {isEmpty ? renderEmpty() : renderPagination()}
    </React.Fragment>
  )
}

export default TermbasePagination
